import React from 'react'
import dayjs from 'dayjs'
import { isReactNativeApp } from '@mobi/web-native-comms/web'
import { NotificationButton } from '@core/Areas/Notifications/NotificationButton/NotificationButton'
import type { RaceStatus } from '@core/Areas/Racing/Hooks/useRaceList/types'
import { buildToteRaceUri } from '../../helpers'

/** Race alert button -  RN only */
export const RaceNotificationButton: React.FC<{
  raceStatus: RaceStatus
  raceTime: Date
  meetingId: string
  raceNumber: number
  meetingName: string
  meetingDate: string
  isHeaderPinned?: boolean
  buttonComponent?: React.FC<React.HTMLAttributes<HTMLButtonElement>>
}> = ({
  raceStatus,
  raceTime,
  meetingId,
  raceNumber,
  meetingDate,
  meetingName,
  isHeaderPinned,
  buttonComponent,
}) => {
  if (!isReactNativeApp && process.env.NODE_ENV !== 'development') {
    return null
  }

  return (
    <RaceNotificationButtonComponent
      raceStatus={raceStatus}
      raceTime={raceTime}
      meetingId={meetingId}
      raceNumber={raceNumber}
      meetingDate={meetingDate}
      meetingName={meetingName}
      isHeaderPinned={isHeaderPinned}
      buttonComponent={buttonComponent}
    />
  )
}

// ===============
// Local Component
// ===============

const RaceNotificationButtonComponent: React.FC<{
  raceStatus: RaceStatus
  raceTime: Date
  meetingId: string
  raceNumber: number
  meetingName: string
  meetingDate: string
  isHeaderPinned?: boolean
  buttonComponent?: React.FC<React.HTMLAttributes<HTMLButtonElement>>
}> = ({
  raceStatus,
  raceTime,
  meetingId,
  raceNumber,
  meetingDate,
  meetingName,
  isHeaderPinned,
  buttonComponent,
}) => {
  const expiryTime = React.useMemo(() => dayjs(raceTime).subtract(1, 'm'), [raceTime])
  const checkIfTimerExpired = React.useCallback(() => expiryTime.isBefore(Date.now()), [expiryTime])

  const [hasTimerExpired, setHasTimerExpired] = React.useState(checkIfTimerExpired)

  React.useEffect(() => {
    const hasExpired = checkIfTimerExpired()
    setHasTimerExpired(hasExpired)

    if (hasExpired) return undefined

    const timerId = setInterval(() => {
      if (!checkIfTimerExpired()) return
      setHasTimerExpired(true)
      clearInterval(timerId)
    }, 1000)
    return () => clearInterval(timerId)
  }, [checkIfTimerExpired])

  const link = React.useMemo(() => {
    const isLocalhost = window.location.origin.includes('localhost')
    const domain = isLocalhost ? '' : window.location.origin

    return (
      domain +
      buildToteRaceUri({
        meetingId,
        meetingDate,
        raceNumber,
      })
    )
  }, [meetingId, meetingDate, raceNumber])

  if (raceStatus === 'Released' || raceStatus === 'Abandoned' || hasTimerExpired) return null

  return (
    <NotificationButton
      ast={raceTime.toISOString()}
      eventId={buildRaceEventId(meetingDate, meetingId, raceNumber)}
      eventType='Race'
      deepLink={link}
      eventName={`${meetingName} - RACE ${raceNumber}`}
      isHeaderPinned={isHeaderPinned}
      buttonComponent={buttonComponent}
    />
  )
}

// this event id should match the format in the kineses event stream
function buildRaceEventId(meetingDate: string, meetingId: string, raceNumber: number) {
  return `${dayjs(meetingDate).format('YYYY-MM-DD')}#${meetingId}#${raceNumber}`
}
